// ─── Rewards settlement helpers (V6-p) ────────────────────────────────────────
//
// Pure functions for the «جوایز» tab, kept out of RewardsTab.jsx so that file
// only exports components (react-refresh) and so they are unit-testable: the
// week/month period keys, what an `admin-rewards_settle` answer means to an
// admin, and the server's refusals in Persian.

import { faDigits } from '../../lib/numerals'
import { MONTHS_FA } from './constants'
import { isPeriodKey as isMonthKey, periodLabel as monthLabel, shiftPeriod as shiftMonth } from './teams'

export const PERIOD_TYPES = [
  { id: 'week', label: 'هفتگی' },
  { id: 'month', label: 'ماهانه' },
]

/** A week key as the backend takes it: `W` + the week's Saturday (Tehran), e.g. `W2026-09-19`. */
const WEEK_KEY = /^W(\d{4})-(\d{2})-(\d{2})$/

function weekStart(key) {
  const match = WEEK_KEY.exec(key ?? '')
  if (!match) return null
  const [, y, m, d] = match.map(Number)
  const date = new Date(Date.UTC(y, m - 1, d))
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) return null
  return date
}

/** A real calendar day that is a Saturday. */
export function isWeekKey(key) {
  const start = weekStart(key)
  return start !== null && start.getUTCDay() === 6
}

function weekKeyOf(date) {
  return `W${date.toISOString().slice(0, 10)}`
}

export function isSettleKey(type, key) {
  return type === 'week' ? isWeekKey(key) : isMonthKey(key)
}

/** The key `delta` periods away; a malformed key comes back as it is. */
export function shiftSettleKey(type, key, delta) {
  if (type === 'month') return shiftMonth(key, delta)
  if (!isWeekKey(key)) return key
  const start = weekStart(key)
  start.setUTCDate(start.getUTCDate() + 7 * delta)
  return weekKeyOf(start)
}

function dayText(date) {
  return { year: date.getUTCFullYear(), text: `${faDigits(date.getUTCDate())} ${MONTHS_FA[date.getUTCMonth()]}` }
}

/**
 * `W2026-09-19` → `هفتهٔ ۱۹ سپتامبر تا ۲۵ سپتامبر ۲۰۲۶`; `1405-07` → `مهر ۱۴۰۵`
 * (a month period *is* a Shamsi month on the backend, so it keeps its Jalali
 * name); anything else as it came.
 */
export function settleLabel(type, key) {
  if (type === 'month') return monthLabel(key)
  if (!isWeekKey(key)) return key || '—'
  const start = weekStart(key)
  const end = new Date(start)
  end.setUTCDate(end.getUTCDate() + 6)
  const from = dayText(start)
  const to = dayText(end)
  return `هفتهٔ ${from.text}${from.year !== to.year ? ` ${faDigits(from.year)}` : ''} تا ${to.text} ${faDigits(to.year)}`
}

/** The example the key input shows. */
export function keyPlaceholder(type) {
  return type === 'week' ? 'W2026-09-19' : '1405-06'
}

/** The runPeriodic job key of a period type's settlement (backend rewards.rules.ts `settleJobKey`). */
export function settleJobKey(type) {
  return `rewards-settle-${type}`
}

/** The switches that keep a real run from paying, in Persian. */
export const DISABLED_FA = {
  'leaderboard.settleEnabled': 'پرداخت جوایز دوره (leaderboard.settleEnabled) خاموش است',
  'gems.enabled': 'اقتصاد جم (gems.enabled) خاموش است',
}

export function describeDisabled(list) {
  return (list ?? []).map(key => DISABLED_FA[key] ?? key)
}

/** From the settings list: which of the two switches are off right now (null while unknown). */
export function switchesOff(settings) {
  if (!Array.isArray(settings)) return null
  const value = key => settings.find(item => item.key === key)?.value === true
  return ['leaderboard.settleEnabled', 'gems.enabled'].filter(key => !value(key))
}

/**
 * What a settle answer means, as `{ tone, text }`. A real run is only sent
 * after a dry run of the same period, so `already-done` is information.
 */
export function describeSettleOutcome(result) {
  switch (result?.outcome) {
    case 'dry-run': return { tone: 'info', text: 'اجرای خشک: هیچ چیزی نوشته نشد. فهرست برندگان را بررسی کنید.' }
    case 'ran': return { tone: 'ok', text: 'تسویهٔ این دوره انجام شد: جم‌ها و نشان‌ها ثبت و به برندگان اعلان داده شد.' }
    case 'already-done': return { tone: 'info', text: 'این دوره قبلاً تسویه شده است؛ دوباره اجرا نشد و هیچ جمی دوباره داده نشد.' }
    case 'already-running': return { tone: 'warn', text: 'تسویهٔ همین دوره همین حالا در حال اجراست. کمی بعد تاریخچه را تازه کنید.' }
    case 'idle': {
      const why = describeDisabled(result.summary?.disabled)
      return { tone: 'warn', text: `هیچ چیزی پرداخت نشد${why.length ? `: ${why.join(' و ')}` : ''}. آنچه نمایش داده می‌شود فقط پیش‌نمایش است و دوره بعداً قابل تسویه می‌ماند.` }
    }
    default: return { tone: 'warn', text: `پاسخ ناشناخته: ${result?.outcome ?? '—'}` }
  }
}

/** The summary of a settle answer — dry-run/idle carry it directly, a real run inside `run`. */
export function settleSummaryOf(result) {
  return result?.summary ?? result?.run?.summary ?? null
}

/** The refusals of `admin-rewards_settle` in Persian, or null (the caller falls back to errorMessage). */
export function settleRefusal(error) {
  const code = error?.response?.data?.error
  if (code === 'period_key_invalid') return 'کلید دوره معتبر نیست: هفته به شکل W + تاریخ شنبه (مثلاً W2026-09-19)، ماه به شکل YYYY-MM (مثلاً 1405-06).'
  if (code === 'period_not_over') return 'این دوره هنوز تمام نشده است؛ فقط اجرای خشک آن ممکن است.'
  return null
}

export const BADGE_FA = {
  weekly_top1: 'نشان اول هفته',
  monthly_top1: 'نشان اول ماه',
  team_weekly_top1: 'نشان تیم اول هفته',
}

export const CATEGORY_FA = { xp: 'جدول XP', teams: 'تیم برتر' }

export const RUN_STATUS_FA = { running: 'در حال اجرا', succeeded: 'موفق', failed: 'ناموفق' }

/** A short, readable form of an id: the first 8 characters. */
export function shortId(id) {
  return typeof id === 'string' ? id.slice(0, 8) : '—'
}
